import { createContext, useContext, useState, useEffect, useRef } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { API, LOGO_URL } from "@/config";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Capture UTM params on first touch so launch traffic is attributable at signup
    try {
      const params = new URLSearchParams(window.location.search);
      const utm = {};
      ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"].forEach((k) => {
        const v = params.get(k);
        if (v) utm[k] = v;
      });
      if (Object.keys(utm).length) localStorage.setItem("creatara_utm", JSON.stringify(utm));
    } catch { /* ignore */ }
    // CRITICAL: Skip auth check if returning from OAuth callback
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // Try cookie-based auth first (Google), then token-based
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true, headers });
      setUser(response.data);
    } catch {
      localStorage.removeItem("token");
      setToken(null);
      setUser(null);
    }
    setLoading(false);
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Direct-to-our-backend Google OAuth (Google Cloud project "Creatara"), so the
  // consent screen reads "Sign in to Creatara" instead of "emergentagent.com".
  const loginWithGoogle = () => {
    const next = encodeURIComponent("/dashboard");
    window.location.href = `${API}/auth/google/login?next=${next}`;
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("token", response.data.access_token);
    setToken(response.data.access_token);
    setUser(response.data.user);
    return response.data;
  };

  const register = async (name, email, password, referralCode) => {
    let utm = null;
    try { utm = JSON.parse(localStorage.getItem("creatara_utm") || "null"); } catch { /* ignore */ }
    const response = await axios.post(`${API}/auth/register`, { name, email, password, referral_code: referralCode || null, utm });
    localStorage.setItem("token", response.data.access_token);
    setToken(response.data.access_token);
    setUser(response.data.user);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const updateUser = (userData) => setUser(userData);
  const setAuthToken = (newToken) => setToken(newToken);

  return <AuthContext.Provider value={{ user, token, login, register, loginWithGoogle, logout, loading, updateUser, setAuthToken, checkAuth }}>
    {loading ? (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background">
        <img src={LOGO_URL} alt="Creatara - Loading Content Monetization Platform" className="w-16 h-16 rounded-xl mb-4 animate-pulse" />
        <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
      </div>
    ) : children}
  </AuthContext.Provider>;
};
